import React, { useState, useEffect } from 'react';
import { UnterweisungenLayout } from './UnterweisungenLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Users, Loader2, Check, X, Filter, Search, Bell, FileDown, Send, CalendarCheck, PenLine, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { ResendInstructionModal } from '../../components/ResendInstructionModal';
import { InstructionSignModal } from '../../components/InstructionSignModal';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper function to calculate deadline urgency
const getDeadlineStatus = (deadline, isConfirmed) => {
  if (isConfirmed) {
    return { color: 'green', label: 'Gelesen', icon: '✅', urgent: false };
  }
  
  if (!deadline) {
    return { color: 'gray', label: 'Keine Frist', icon: '⏳', urgent: false };
  }
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const deadlineDate = new Date(deadline);
  deadlineDate.setHours(0, 0, 0, 0);
  
  const diffTime = deadlineDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) {
    return { color: 'red', label: 'Überfällig', icon: '🔴', urgent: true, days: Math.abs(diffDays) };
  } else if (diffDays === 0) {
    return { color: 'orange', label: 'Heute fällig', icon: '🟠', urgent: true, days: 0 };
  } else if (diffDays === 1) {
    return { color: 'orange', label: 'Morgen fällig', icon: '🟠', urgent: true, days: 1 };
  } else if (diffDays <= 3) {
    return { color: 'yellow', label: 'Bald fällig', icon: '🟡', urgent: false, days: diffDays };
  } else {
    return { color: 'green', label: 'Zeit vorhanden', icon: '🟢', urgent: false, days: diffDays };
  }
};

export const UnterweisungenMitarbeiter = () => {
  const [trackingData, setTrackingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all'); // NEW: Status filter
  const [searchTerm, setSearchTerm] = useState('');
  const [sendingReminder, setSendingReminder] = useState(null);
  const [resendModal, setResendModal] = useState(null);

  // "Persönlich unterwiesen am" Modal
  const [unterwiesenModal, setUnterwiesenModal] = useState(null);
  const [unterwiesenDate, setUnterwiesenDate] = useState('');
  const [savingUnterwiesen, setSavingUnterwiesen] = useState(false);

  // MA-Unterschrift nachholen
  const [maSignModal, setMaSignModal] = useState(null);

  // Mitarbeiter rückwirkend aus Unterweisung entfernen
  const [removeConfirm, setRemoveConfirm] = useState(null); // { instruction_id, employee_id, employee_name, instruction_title }
  const [removing, setRemoving] = useState(false);

  const handleRemoveEmployee = async () => {
    if (!removeConfirm) return;
    setRemoving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/api/instructions/${removeConfirm.instruction_id}/mitarbeiter/${removeConfirm.employee_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${removeConfirm.employee_name} wurde aus der Unterweisung entfernt`);
      setRemoveConfirm(null);
      fetchTracking();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Entfernen');
    } finally {
      setRemoving(false);
    }
  };

  // Akkordeon: welche Mitarbeiter sind aufgeklappt (Standard: alle zu)
  const [expandedEmployees, setExpandedEmployees] = useState(new Set());

  const toggleEmployee = (employeeId) => {
    setExpandedEmployees(prev => {
      const next = new Set(prev);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      return next;
    });
  };

  useEffect(() => {
    fetchTracking();
  }, []);

  const fetchTracking = async () => {
    try {
      const response = await axios.get(`${API}/api/instructions/tracking`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setTrackingData(response.data || []);
    } catch (error) {
      console.error('Failed to fetch tracking:', error);
      toast.error('Fehler beim Laden der Tracking-Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminder = async (instructionId, employeeId, employeeName) => {
    const key = `${instructionId}_${employeeId}`;
    setSendingReminder(key);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/instructions/${instructionId}/remind/${employeeId}`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      toast.success(`✅ Erinnerung gesendet an ${employeeName}`);
      
      // Refresh data to show updated reminder
      fetchTracking();
    } catch (error) {
      console.error('Failed to send reminder:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Senden der Erinnerung');
    } finally {
      setSendingReminder(null);
    }
  };

  const openUnterwiesenModal = (instruction) => {
    setUnterwiesenModal({
      instruction_id: instruction.instruction_id,
      employee_id: instruction.employee_id,
      employee_name: instruction.employee_name,
      current_date: instruction.unterwiesen_am || ''
    });
    setUnterwiesenDate(instruction.unterwiesen_am ? instruction.unterwiesen_am.split('T')[0] : '');
  };

  const handleSaveUnterwiesen = async () => {
    if (!unterwiesenModal) return;
    setSavingUnterwiesen(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/instructions/${unterwiesenModal.instruction_id}/mitarbeiter/${unterwiesenModal.employee_id}/unterwiesen-datum`,
        { unterwiesen_am: unterwiesenDate || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Datum für ${unterwiesenModal.employee_name} gespeichert`);
      setUnterwiesenModal(null);
      fetchTracking();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingUnterwiesen(false);
    }
  };

  // Get unique categories and employees
  const categories = [...new Set(trackingData.map(item => item.instruction_category))];
  const employees = [...new Set(trackingData.map(item => item.employee_name))];

  // Filter data
  const filteredData = trackingData.filter(item => {
    const matchesCategory = filterCategory === 'all' || item.instruction_category === filterCategory;
    const matchesEmployee = filterEmployee === 'all' || item.employee_name === filterEmployee;
    const matchesStatus = 
      filterStatus === 'all' || 
      (filterStatus === 'confirmed' && item.is_confirmed) ||
      (filterStatus === 'pending' && !item.is_confirmed);
    const matchesSearch = searchTerm === '' || 
      item.instruction_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.employee_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCategory && matchesEmployee && matchesStatus && matchesSearch;
  });

  // Group by employee
  const groupedByEmployee = {};
  filteredData.forEach(item => {
    if (!groupedByEmployee[item.employee_name]) {
      groupedByEmployee[item.employee_name] = {
        employee_id: item.employee_id,
        employee_email: item.employee_email,
        instructions: []
      };
    }
    groupedByEmployee[item.employee_name].instructions.push(item);
  });

  // Calculate stats
  const totalAssignments = trackingData.length;
  const totalConfirmed = trackingData.filter(item => item.is_confirmed).length;
  const confirmationRate = totalAssignments > 0 ? Math.round((totalConfirmed / totalAssignments) * 100) : 0;

  if (loading) {
    return (
      <UnterweisungenLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </UnterweisungenLayout>
    );
  }

  return (
    <UnterweisungenLayout>
      <div className="space-y-6">
        {/* Export Button - Top Bar - Mobile Optimized */}
        <div className="flex justify-end">
          <Button
            onClick={() => window.location.href = '/admin/unterweisungen/export'}
            className="bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm px-3 py-2 sm:px-4 sm:py-2"
          >
            <FileDown className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
            <span className="hidden xs:inline">Unterweisungen Export</span>
            <span className="inline xs:hidden">Export</span>
          </Button>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Zuweisungen gesamt</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">{totalAssignments}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Bestätigt</div>
              <div className="text-2xl font-bold text-green-600 mt-1">{totalConfirmed}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">Bestätigungsrate</div>
              <div className="text-2xl font-bold text-blue-600 mt-1">{confirmationRate}%</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-3 sm:p-4">
            {/* Search + Dropdowns */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Category Filter */}
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="all">Alle Kategorien</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>

              {/* Employee Filter */}
              <select
                value={filterEmployee}
                onChange={(e) => setFilterEmployee(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="all">Alle Mitarbeiter</option>
                {employees.map(emp => (
                  <option key={emp} value={emp}>{emp}</option>
                ))}
              </select>
            </div>

            {/* Status Toggle Buttons - NEW */}
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => setFilterStatus('all')}
                className={`flex-1 sm:flex-none px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
                  filterStatus === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Alle
              </button>
              <button
                onClick={() => setFilterStatus('confirmed')}
                className={`flex-1 sm:flex-none px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-1 ${
                  filterStatus === 'confirmed'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Check className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Erledigt</span>
                <span className="sm:hidden">✓</span>
              </button>
              <button
                onClick={() => setFilterStatus('pending')}
                className={`flex-1 sm:flex-none px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-1 ${
                  filterStatus === 'pending'
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <X className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Offen</span>
                <span className="sm:hidden">⏳</span>
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Content */}
        {Object.keys(groupedByEmployee).length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="p-4 bg-blue-100 rounded-full mb-4">
                <Users className="h-12 w-12 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Keine Tracking-Daten</h2>
              <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
                {trackingData.length === 0 
                  ? 'Erstellen Sie Unterweisungen und weisen Sie diese Mitarbeitern zu.'
                  : 'Keine Ergebnisse für die aktuellen Filter.'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedByEmployee).map(([employeeName, data]) => {
              const confirmedCount = data.instructions.filter(i => i.is_confirmed).length;
              const totalCount = data.instructions.length;
              const rate = Math.round((confirmedCount / totalCount) * 100);
              
              return (
                <Card key={data.employee_id} className="overflow-hidden">
                  {/* Klickbarer Mitarbeiter-Header (Akkordeon) */}
                  <button
                    onClick={() => toggleEmployee(data.employee_id)}
                    className="w-full text-left p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                    data-testid={`employee-accordion-${data.employee_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-full">
                        <Users className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">{employeeName}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{data.employee_email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className={`text-xl font-bold ${rate === 100 ? 'text-green-600' : rate >= 50 ? 'text-blue-600' : 'text-orange-600'}`}>
                          {rate}%
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">{confirmedCount}/{totalCount}</div>
                      </div>
                      {expandedEmployees.has(data.employee_id)
                        ? <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" />
                        : <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      }
                    </div>
                  </button>

                  {/* Aufgeklappte Unterweisungen */}
                  {expandedEmployees.has(data.employee_id) && (
                  <CardContent className="pt-0 px-4 pb-4 border-t">
                    <div className="space-y-2 mt-3">
                      {data.instructions.map(instruction => {
                        const status = getDeadlineStatus(instruction.deadline, instruction.is_confirmed);
                        
                        return (
                        <div 
                          key={instruction.instruction_id}
                          className={`p-2 sm:p-3 rounded-lg ${
                            status.urgent ? 'bg-red-50 border border-red-200' : 'bg-gray-50'
                          }`}
                        >
                          {/* Mobile: Vertical Layout | Desktop: Horizontal */}
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
                            
                            {/* Left: Title & Meta */}
                            <div className="flex-1 min-w-0">
                              {/* Title Row */}
                              <div className="flex items-center gap-2 mb-1.5">
                                <span className="text-sm flex-shrink-0">{status.icon}</span>
                                <div className="font-medium text-gray-900 dark:text-gray-100 text-sm sm:text-base line-clamp-1">
                                  {instruction.instruction_title}
                                </div>
                              </div>
                              
                              {/* Badges Row - Wrapped */}
                              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                                <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded whitespace-nowrap">
                                  {instruction.instruction_category}
                                </span>
                                {instruction.deadline && (
                                  <span className={`text-xs px-2 py-0.5 rounded font-medium whitespace-nowrap ${
                                    status.color === 'red' ? 'bg-red-100 text-red-700' :
                                    status.color === 'orange' ? 'bg-orange-100 text-orange-700' :
                                    status.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-green-100 text-green-700'
                                  }`}>
                                    Frist: {new Date(instruction.deadline).toLocaleDateString('de-DE')}
                                  </span>
                                )}
                                <span className="text-xs text-gray-500 whitespace-nowrap">
                                  Erstellt: {new Date(instruction.created_at).toLocaleDateString('de-DE')}
                                </span>
                              </div>
                            </div>
                            
                            {/* Right: Status & Actions */}
                            <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3 sm:flex-shrink-0">
                              {/* Kombinierter Status: Gelesen + Pers. unterwiesen */}
                              <div className="flex flex-col gap-1 items-end">
                                {instruction.is_confirmed ? (
                                  <div className="flex items-center gap-1.5 text-green-600">
                                    <Check className="h-4 w-4" />
                                    <div className="text-xs">
                                      <div className="font-medium">Gelesen</div>
                                      <div className="text-gray-500">{new Date(instruction.confirmed_at).toLocaleDateString('de-DE')}</div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1.5 text-orange-500">
                                    <X className="h-4 w-4" />
                                    <div className="text-xs">
                                      <div className={`font-medium ${status.urgent ? 'text-red-600' : 'text-orange-600'}`}>{status.label}</div>
                                    </div>
                                  </div>
                                )}
                                {instruction.unterwiesen_am ? (
                                  <div className="flex items-center gap-1.5 text-blue-600">
                                    <CalendarCheck className="h-3.5 w-3.5" />
                                    <div className="text-xs">
                                      <div className="font-medium">Pers. unterwiesen</div>
                                      <div className="text-gray-500">{new Date(instruction.unterwiesen_am.split('T')[0] + 'T12:00:00').toLocaleDateString('de-DE')}</div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-xs text-gray-400 italic">Noch nicht pers. unterwiesen</div>
                                )}
                              </div>

                              {/* Buttons */}
                              <div className="flex flex-col gap-1">
                                {/* Datum-Button für "Persönlich unterwiesen" */}
                                <button
                                  onClick={() => openUnterwiesenModal(instruction)}
                                  data-testid={`unterwiesen-btn-${instruction.instruction_id}-${instruction.employee_id}`}
                                  className="p-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors flex-shrink-0"
                                  title="Persönlich unterwiesen am – Datum eintragen"
                                >
                                  <CalendarCheck className="h-3.5 w-3.5" />
                                </button>
                                {/* MA-Unterschrift nachholen */}
                                <button
                                  onClick={() => setMaSignModal({
                                    instruction_id: instruction.instruction_id,
                                    employee_id: instruction.employee_id,
                                    employee_name: instruction.employee_name,
                                    instruction_title: instruction.instruction_title,
                                  })}
                                  data-testid={`ma-sign-btn-${instruction.instruction_id}-${instruction.employee_id}`}
                                  className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${instruction.has_signature_ma ? 'bg-green-50 text-green-600 hover:bg-green-100' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}`}
                                  title={instruction.has_signature_ma ? 'MA hat unterschrieben – erneut' : 'MA-Unterschrift nachholen'}
                                >
                                  <PenLine className="h-3.5 w-3.5" />
                                </button>
                                {/* Rückwirkend aus Unterweisung entfernen */}
                                <button
                                  onClick={() => setRemoveConfirm({
                                    instruction_id: instruction.instruction_id,
                                    employee_id: instruction.employee_id,
                                    employee_name: instruction.employee_name,
                                    instruction_title: instruction.instruction_title,
                                  })}
                                  data-testid={`remove-assignment-btn-${instruction.instruction_id}-${instruction.employee_id}`}
                                  className="p-1.5 rounded-lg bg-gray-50 text-gray-300 hover:bg-red-50 hover:text-red-500 transition-colors flex-shrink-0"
                                  title="Mitarbeiter aus dieser Unterweisung entfernen"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                                {/* Erinnerung (nur wenn nicht gelesen) */}
                                {!instruction.is_confirmed && (
                                  <button
                                    onClick={() => handleSendReminder(instruction.instruction_id, instruction.employee_id, instruction.employee_name)}
                                    disabled={sendingReminder === `${instruction.instruction_id}_${instruction.employee_id}`}
                                    className="p-1.5 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                                    title="Erinnerung senden"
                                  >
                                    {sendingReminder === `${instruction.instruction_id}_${instruction.employee_id}` ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                      <Bell className="h-3.5 w-3.5" />
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )})}
                    </div>
                  </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Results Count */}
        {filteredData.length !== trackingData.length && (
          <div className="text-center text-sm text-gray-600 dark:text-gray-400">
            {filteredData.length} von {trackingData.length} Einträgen angezeigt
          </div>
        )}
      </div>

      {/* Modal: Persönlich unterwiesen am – Datum eintragen */}
      <Dialog open={!!unterwiesenModal} onOpenChange={() => setUnterwiesenModal(null)}>        <DialogContent className="max-w-sm" data-testid="unterwiesen-datum-modal">
          <DialogHeader>
            <DialogTitle>Persönlich unterwiesen am</DialogTitle>
          </DialogHeader>
          <div className="py-3 space-y-3">
            {unterwiesenModal && (
              <p className="text-sm text-muted-foreground">
                Mitarbeiter: <span className="font-semibold text-foreground">{unterwiesenModal.employee_name}</span>
              </p>
            )}
            <div className="space-y-1">
              <label className="text-sm font-medium">Datum der persönlichen Unterweisung</label>
              <input
                type="date"
                value={unterwiesenDate}
                onChange={(e) => setUnterwiesenDate(e.target.value)}
                data-testid="unterwiesen-date-input"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {unterwiesenDate && (
                <p className="text-xs text-blue-600">
                  Eintrag: {new Date(unterwiesenDate + 'T12:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}
                </p>
              )}
            </div>
            {unterwiesenDate && (
              <button
                type="button"
                onClick={() => setUnterwiesenDate('')}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Datum löschen
              </button>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setUnterwiesenModal(null)}>Abbrechen</Button>
            <Button
              onClick={handleSaveUnterwiesen}
              disabled={savingUnterwiesen}
              data-testid="save-unterwiesen-btn"
            >
              {savingUnterwiesen && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bestätigungs-Dialog: Mitarbeiter aus Unterweisung entfernen */}
      <Dialog open={!!removeConfirm} onOpenChange={() => setRemoveConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="remove-assignment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Zuweisung entfernen?
            </DialogTitle>
          </DialogHeader>
          <div className="py-3 space-y-2 text-sm text-muted-foreground">
            {removeConfirm && (
              <>
                <p>Möchtest du <strong className="text-foreground">{removeConfirm.employee_name}</strong> rückwirkend aus der Unterweisung entfernen?</p>
                <p className="bg-muted/50 rounded-lg p-2 text-xs">
                  Unterweisung: <strong>{removeConfirm.instruction_title}</strong>
                </p>
                <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg p-2">
                  ⚠️ Dies löscht die Zuweisung, alle Bestätigungen und Unterschriften dieser Person für diese Unterweisung.
                </p>
              </>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRemoveConfirm(null)}>Abbrechen</Button>
            <Button
              variant="destructive"
              onClick={handleRemoveEmployee}
              disabled={removing}
              data-testid="confirm-remove-btn"
            >
              {removing && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Ja, entfernen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MA-Unterschrift nachholen (Admin setzt für Mitarbeiter vor Ort) */}
      {maSignModal && (
        <InstructionSignModal
          open={!!maSignModal}
          onClose={() => setMaSignModal(null)}
          title={maSignModal.instruction_title}
          signerLabel={`Mitarbeiter: ${maSignModal.employee_name}`}
          signerName={maSignModal.employee_name}
          confirmLabel="Unterschrift speichern"
          isEmployee={true}
          onSign={async (sig) => {
            const token = localStorage.getItem('token');
            await axios.put(
              `${API}/api/instructions/${maSignModal.instruction_id}/mitarbeiter/${maSignModal.employee_id}/sign-ma`,
              { signature_ma: sig },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Unterschrift für ${maSignModal.employee_name} gespeichert`);
            setMaSignModal(null);
            fetchTracking();
          }}
        />
      )}

    </UnterweisungenLayout>
  );
};
